import React, {useContext, useState} from "react";
import axios from "axios";

// Models
import {SubscriptionDTO} from "../../../Models/DTOs/Subscription";
import {AuthUser} from "../../../Models/DTOs/AuthUser";

// Context
import {UserAuthenticationContext} from "../../../Context/UserAuthenticationContext";

// Helpers
import {formatMoney} from "../../../Helpers/Currency/Currency";
import {
    SubscriptionStatusAccentColor,
    SubscriptionStatusColor,
    HasLifetimeMembership, isUsingIntroPrice, IsSubscriptionActive, IsSubscriptionNegativeStatus,
    canPauseSubscription, resumeChargesImmediately, normalizePauseError, hasContentAccess
} from "../../../Helpers/Subscription";
import {capitalizeFirst} from "../../../Helpers/DataTypesGeneral/Strings";
import {toGBDate} from "../../../Helpers/DataTypesGeneral/DateAndTime";

// Components
import ButtonPill from "../../Buttons/ButtonPill";
import ButtonPillGradient from "../../Buttons/ButtonPillGradient";
import PopUp from "../../Popups/PopUp";
import PauseMembership from "../../Forms/PauseMembership/PauseMembership";

// Api
import {ResumeSubscription, UnpauseSubscription} from "../../../Api/Stripe";

// Assets
import IconLoaderCircle from "../../../Assets/SVGs/Icons/Loading/LoaderCircle";
import IconAlertError from "../../../Assets/SVGs/Icons/AlertAndInfo/AlertError";
import IconCheckMarkHollow from "../../../Assets/SVGs/Icons/YesOrNo/CheckMarkHollow";
import CancelOrPause from "../../Forms/CancelOrPause/CancelOrPause";
import PaymentMethod from "../../Forms/Account/PaymentMethod";

// Constants
import {RoutePaths} from "../../../Constants/RoutePaths";

type AccountSubInfoProps = {
    sub: SubscriptionDTO | null;
    userData: AuthUser | null;
    isSubLoading: boolean;
};

const AccountSubInfo: React.FC<AccountSubInfoProps> = (
    {
        sub,
        userData,
        isSubLoading
    }
) => {
    const
        {getValidAccessToken} = useContext(UserAuthenticationContext),
        [isLoading, setIsLoading] = useState<boolean>(false),
        [subUpdateRequested, setSubUpdateRequested] = useState<boolean>(false),
        [isCancelOrPauseOpen, setIsCancelOrPauseOpen] = useState<boolean>(false),
        [isPauseOpen, setIsPauseOpen] = useState<boolean>(false),
        [isPaymentMethodOpen, setIsPaymentMethodOpen] = useState<boolean>(false),
        [showUpdateForm, setShowUpdateForm] = useState(false),
        [error, setError] = useState<string>(""),

        canceledAt = sub?.CanceledAtUtc ? new Date(sub.CanceledAtUtc) : null,
        cancelAt = sub?.CancelAtUtc ? new Date(sub.CancelAtUtc) : null,
        renewAt = sub?.CurrentPeriodEndUtc ? new Date(sub.CurrentPeriodEndUtc) : null,

        now = new Date(),
        controller = new AbortController(),

        buttonIcon = () => {
            if (isLoading) {
                return IconLoaderCircle({className: 'icon--loader'});
            } else if (!isLoading && error !== "") {
                return IconAlertError({className: 'icon--error'});
            } else if (!isLoading && error === "" && subUpdateRequested) {
                return IconCheckMarkHollow({className: 'icon--success'});
            } else return null;
        },

        closePMPopUp = () => {
            setIsPaymentMethodOpen(false);
            setShowUpdateForm(false);
        },

        resetCancelOrPauseForm = () => {
            setIsCancelOrPauseOpen(false);
            document.dispatchEvent(new Event("resetCancelSteps"));
        },

        resetPaymentMethodForm = () => {
            closePMPopUp();
        },

        handleResume = async () => {
            if (sub?.UserId) {
                try {
                    setIsLoading(true);
                    setSubUpdateRequested(true);
                    // resume is self-or-admin protected — attach a valid token.
                    const accessToken = await getValidAccessToken();
                    if (!accessToken) {
                        setError("Membership wasn’t changed — your session has expired. Please log in and try again.");
                        return;
                    }
                    const res = await ResumeSubscription(sub?.UserId, accessToken, controller);
                    if (axios.isAxiosError(res)) {
                        console.error("Resume failed:", res);
                        setError("Membership wasn’t changed, contact support@ickonic.com");
                        return;
                    }

                    if (res.resumed) {
                        setTimeout(() => window.location.reload(), 3000);
                    } else {
                        setError("Membership wasn’t changed, contact support@ickonic.com");
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        },

        // Resume early from a pause. During the freeze window this starts a new
        // billing period and charges immediately, so pre-warn the user first.
        handleUnpause = async () => {
            const userId = userData?.id ?? sub?.UserId ?? "";
            if (!userId) return;

            if (
                resumeChargesImmediately(sub, now) &&
                !window.confirm("Resuming now starts a new billing period and charges you today. Continue?")
            ) {
                return;
            }

            try {
                setIsLoading(true);
                setSubUpdateRequested(true);
                setError("");

                const token = await getValidAccessToken();
                if (!token) {
                    setError("Your session has expired. Please sign in again.");
                    return;
                }

                const res = await UnpauseSubscription(userId, token, controller);
                if (axios.isAxiosError(res)) {
                    console.error("Unpause failed:", res);
                    setError(normalizePauseError(res));
                    return;
                }

                if (res.unpaused) {
                    setTimeout(() => window.location.reload(), 3000);
                } else {
                    setError("Membership wasn’t changed, contact support@ickonic.com");
                }
            } finally {
                setIsLoading(false);
            }
        },

        status = sub?.Status ?? "",
        isPaused = !!sub?.IsPaused || status === "paused",
        // Lifetime detection must not rely solely on the Stripe-mirror subscription
        // (`sub`): that endpoint is now bearer-gated and returns no lifetime row for
        // one-time lifetime purchases. The auth service is the app-wide source of
        // truth for plan identity (see Header / VideoAccessOverlay), so prefer it and
        // fall back to `sub` so detection never regresses.
        isLifetime =
            userData?.subscription?.plan_key === "PLAN_LIFETIME" ||
            HasLifetimeMembership(userData?.subscription?.plan_id) ||
            HasLifetimeMembership(sub?.PlanId),
        isMonthly = sub?.BillingInterval === "month" && (sub?.BillingIntervalCount ?? 1) === 1,
        hasFutureCancel = IsSubscriptionActive(sub?.Status) && !!cancelAt && cancelAt.getTime() > now.getTime(),
        isFullyCancelled = !IsSubscriptionActive(sub?.Status) || !!canceledAt,
        isActiveLike = status === "active" || status === "trialing",
        // A lifetime member with no Stripe-mirror row must not reserve space for a
        // resume/upgrade button that never renders, so exclude them from the last term.
        willShowAButton = isActiveLike && isMonthly || hasFutureCancel || isFullyCancelled || isPaused || (!isSubLoading && !sub && !isLifetime),

        customerCanCancel = () => {
            // 1. If the sub status isn't already "cancelled"
            // 2. If the subscription at least has 1 paid invoice
            // 3. The user hasn't already requested a cancel
            // 4. Customer isn't a lifetime member
            return (
                sub &&
                !IsSubscriptionNegativeStatus(sub.Status) &&
                sub?.SubscriptionStartedUtc !== null &&
                !cancelAt && !isLifetime
            );
        },

        renderResumeUpgradeButton = () => {
            if ((isSubLoading && !sub?.UserId) || isLifetime) return null;

            if (!isSubLoading && !sub) {
                return (
                    <ButtonPillGradient
                        link={RoutePaths.ChoosePlan}
                        text="Subscribe to Ickonic"
                        icon={buttonIcon()}
                        className={`account__reactivate-membership account__upgrade-membership ${isLoading ? 'is-loading' : 'is-not-loading'} ${subUpdateRequested ? 'has-update-request' : 'has-no-request'}`}
                        color="white"
                        background="linear-gradient(225deg, #4676E6 0%, #B760B4 100%)"
                        backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #B760B4 100%)"
                        disabled={isLoading}
                    />
                );
            }

            // 0) Paused -> Resume (early). Must come before the cancelled checks,
            // since a paused sub reads as "not active" and would otherwise fall
            // through to Restart.
            if (isPaused) {
                return (
                    <ButtonPillGradient
                        text="Resume Membership"
                        icon={buttonIcon()}
                        className={`account__reactivate-membership account__resume-membership ${isLoading ? 'is-loading' : 'is-not-loading'} ${subUpdateRequested ? 'has-update-request' : 'has-no-request'}`}
                        color="white"
                        background="linear-gradient(225deg, #4676E6 0%, #B760B4 100%)"
                        backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #B760B4 100%)"
                        onClick={handleUnpause}
                        disabled={isLoading}
                    />
                );
            }

            // 1) Active monthly -> Upgrade
            if (isActiveLike && isMonthly && !hasFutureCancel) {
                return (
                    <ButtonPillGradient
                        link={RoutePaths.ChoosePlan}
                        text="Upgrade Membership"
                        icon={buttonIcon()}
                        className={`account__reactivate-membership account__upgrade-membership ${isLoading ? 'is-loading' : 'is-not-loading'} ${subUpdateRequested ? 'has-update-request' : 'has-no-request'}`}
                        color="white"
                        background="linear-gradient(225deg, #4676E6 0%, #B760B4 100%)"
                        backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #B760B4 100%)"
                        disabled={isLoading}
                    />
                );
            }

            // 2) cancelAt in the future -> Resume
            if (hasFutureCancel) {
                return (
                    <ButtonPillGradient
                        text="Resume Membership"
                        icon={buttonIcon()}
                        className={`account__reactivate-membership account__resume-membership ${isLoading ? 'is-loading' : 'is-not-loading'} ${subUpdateRequested ? 'has-update-request' : 'has-no-request'}`}
                        color="white"
                        background="linear-gradient(225deg, #4676E6 0%, #B760B4 100%)"
                        backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #B760B4 100%)"
                        onClick={handleResume}
                        disabled={isLoading}
                    />
                );
            }

            // 3) fully cancelled -> Restart (navigate to the new page)
            if (isFullyCancelled) {
                return (
                    <ButtonPillGradient
                        link={RoutePaths.ChoosePlan}
                        text="Restart Membership"
                        icon={buttonIcon()}
                        className={`account__reactivate-membership account__restart-membership ${isLoading ? 'is-loading' : 'is-not-loading'} ${subUpdateRequested ? 'has-update-request' : 'has-no-request'}`}
                        color="white"
                        background="linear-gradient(225deg, #4676E6 0%, #B760B4 100%)"
                        backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #B760B4 100%)"
                        disabled={isLoading}
                    />
                );
            }

            return null;
        };

    let nextDateLabel = "Next renewal";
    let nextDate: Date | null = renewAt;

    // 1) If cancelAt exists and is in the future → "Cancels on"
    // 2) If cancelAt exists and is in the past  → "Canceled on"
    // 3) Otherwise → "Next renewal" (renewAt)
    if (cancelAt) {
        if (cancelAt.getTime() > now.getTime()) {
            nextDateLabel = "Cancels on";
            nextDate = cancelAt;
        } else {
            nextDateLabel = "Canceled on";
            nextDate = cancelAt;
        }
    } else if (canceledAt) {
        // Optional safety: handle immediate cancels where CancelAtUtc may be null
        nextDateLabel = "Canceled on";
        nextDate = canceledAt;
    }

    return (
        <div className="account__sub-info">
            <h3>
                Current Plan
            </h3>

            <div className="account__plan-container">
                <div
                    className={`account__plan-info account__price-info ${willShowAButton ? 'has-button' : 'has-no-button'} ${isLifetime ? 'has-lifetime-subscription' : 'has-normal-subscription'}`}>
                    <div className="plan-info__name-and-trial">
                        <p className="plan-info__name">
                            {
                                isSubLoading ?
                                    "Loading"
                                    :
                                    (
                                        isLifetime ?
                                            "Lifetime Member"
                                            :
                                            (sub ? sub?.PriceNickname : "No Active Subscription")
                                    )
                            }
                        </p>

                        {
                            sub && IsSubscriptionActive(sub.Status) && isUsingIntroPrice(sub) &&
                            (
                                <p className="plan-info__trial">
                                    {formatMoney(sub?.LastInvoiceTotalAmount, sub?.PriceCurrency)} Intro Period Active
                                </p>
                            )
                        }
                    </div>

                    <p className="plan-info__price">
                        {!isSubLoading && (sub || isLifetime) && (
                            <>
                                {!isLifetime && <span className="plan-info__price-label">Latest invoice: </span>}
                                {isLifetime ? "Thank you!" : formatMoney(sub?.LastInvoiceTotalAmount, sub?.LastInvoiceCurrency ?? sub?.PriceCurrency) ?? "—"} {!isLifetime &&
                                <span className="plan-info__billing-period">Current period: {sub?.BillingIntervalCount ?? 1} {sub?.BillingInterval}{(sub?.BillingIntervalCount ?? 1) > 1 ? "s" : ""}</span>}
                            </>
                        )}
                    </p>

                    {renderResumeUpgradeButton()}

                    {!isSubLoading && (sub || isLifetime) && (
                        <p className="plan-info__sticker" style=
                            {
                                {
                                    background: isLifetime ? SubscriptionStatusColor('active') : cancelAt && sub?.Status === "active" ? SubscriptionStatusColor("will-cancel") : SubscriptionStatusColor(sub?.Status),
                                    color: isLifetime ? SubscriptionStatusAccentColor('active') : cancelAt && sub?.Status === "active" ? SubscriptionStatusAccentColor("will-cancel") : SubscriptionStatusAccentColor(sub?.Status)
                                }
                            }
                        >
                            {
                                isLifetime ? "Active" : cancelAt && sub?.Status === "active" ? "Cancel Scheduled" : sub?.Status === "" ? "Pending" : capitalizeFirst(sub?.Status).replace('_',' ')
                            }
                        </p>
                    )}

                    {!isSubLoading && sub && isPaused && (
                        <p className="plan-info__pause-note">
                            {hasContentAccess(sub, now)
                                ? `You keep access until ${toGBDate(sub.PauseAccessUntilUtc)}. Auto-resumes ${toGBDate(sub.PauseResumesAtUtc)}.`
                                : `Membership frozen — auto-resumes ${toGBDate(sub.PauseResumesAtUtc)}. Resume early to regain access now.`}
                        </p>
                    )}
                </div>

                <div
                    className={`account__plan-info account__date-info ${sub?.SubscriptionStartedUtc ? 'has-three-columns' : 'has-two-columns'}`}>
                    {sub?.SubscriptionStartedUtc && (
                        <div className="account__date-item">
                            <p>
                                Subscription Start
                            </p>

                            <p className="plan-info__date">
                                {toGBDate(sub?.SubscriptionStartedUtc)}
                            </p>
                        </div>
                    )}

                    <div className="account__date-item">
                        <p>
                        Last renewal
                        </p>

                        <p className="plan-info__date">
                            {toGBDate(sub?.CurrentPeriodStartUtc)}
                        </p>
                    </div>

                    <div className="account__date-item">
                        <p>
                            {nextDateLabel}
                        </p>

                        <p className="plan-info__date">
                            {nextDate && !isLifetime ? toGBDate(nextDate.toISOString()) : "—"}
                        </p>
                    </div>
                </div>
            </div>

            {canPauseSubscription(sub) && (
                <ButtonPill
                    label="Pause Membership"
                    className="account__button account__pause-sub"
                    onClick={() => setIsPauseOpen(true)}
                />
            )}

            {customerCanCancel() && (
                <ButtonPill
                    label="Cancel Membership"
                    className="account__button account__cancel-sub"
                    onClick={() => setIsCancelOrPauseOpen(true)}
                />
            )}

            {!isLifetime && (
                <ButtonPill
                    label="Manage Payment Method"
                    className="account__button account__payment-method"
                    onClick={() => setIsPaymentMethodOpen(true)}
                />
            )}

            <PopUp
                canBeDismissed={true}
                isFullScreen={true}
                isOpen={isCancelOrPauseOpen}
                onClose={() => resetCancelOrPauseForm()}
            >
                {isCancelOrPauseOpen && (
                    <CancelOrPause
                        sub={sub}
                        userData={userData}
                    />
                )}
            </PopUp>

            <PopUp
                canBeDismissed={true}
                isFullScreen={true}
                isOpen={isPauseOpen}
                onClose={() => setIsPauseOpen(false)}
            >
                <PauseMembership
                    sub={sub}
                    userData={userData}
                    getValidAccessToken={() => getValidAccessToken()}
                />
            </PopUp>

            <PopUp
                canBeDismissed={true}
                isFullScreen={true}
                isOpen={isPaymentMethodOpen}
                onClose={() => resetPaymentMethodForm()}
            >
                {userData?.id && (
                    <PaymentMethod
                        userId={userData?.id}
                        getValidAccessToken={getValidAccessToken}
                        closePaymentMethodPopUp={() => closePMPopUp()}
                        showUpdateForm={showUpdateForm}
                        setShowUpdateForm={(show) => setShowUpdateForm(show)}
                    />
                )}
            </PopUp>
        </div>
    );
}

export default AccountSubInfo;
